// src/event/brandHead.js — §18 #8: runtime <head> branding for the resolved, LAUNCHED brand.
// Mirrored behavior in both repos (door + portal); this copy uses single quotes + a portal
// DOC_TITLE_SUFFIX (" - Host Portal", a plain hyphen to match the legacy static <title>). Keep the two in sync.
//
// Runs synchronously at import (imported FIRST in the bootstrap, before render / SW registration)
// so the very first paint and iOS Add-to-Home-Screen see the right brand. It ONLY applies for a
// status:"resolved" brand that is in BRAND_DISPLAY (i.e. launched/active). A draft/embedded brand
// (panda while unlaunched) or an unknown host keeps the brand-NEUTRAL static head + neutral
// /manifest.webmanifest — matching the brandManifest function, so an unlaunched brand is never
// exposed. Per-brand icons stay the bundled set until Firebase Storage is activated (#8 deferred).
import { resolveBrand, BRAND_DISPLAY } from './resolveEventId';

const DOC_TITLE_SUFFIX = ' - Host Portal'; // door copy uses "" (plain hyphen matches the legacy tab title)

function setMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function findLink(rel, href, asValue) {
  return Array.from(document.head.querySelectorAll(`link[rel="${rel}"]`)).find((el) => {
    if (el.getAttribute('href') !== href) return false;
    return !asValue || el.getAttribute('as') === asValue;
  });
}

function addPreconnect(href) {
  try {
    const origin = new URL(href, window.location.origin).origin;
    if (origin === window.location.origin || findLink('preconnect', origin)) return;
    const el = document.createElement('link');
    el.setAttribute('rel', 'preconnect');
    el.setAttribute('href', origin);
    document.head.appendChild(el);
  } catch {
    /* ignore invalid icon URLs */
  }
}

function preloadImage(href) {
  if (!href || findLink('preload', href, 'image')) return;
  const el = document.createElement('link');
  el.setAttribute('rel', 'preload');
  el.setAttribute('as', 'image');
  el.setAttribute('href', href);
  document.head.appendChild(el);
}

export function applyBrandHead(hostname, search) {
  try {
    const { eventId, status } = resolveBrand(hostname, search);
    // Only brand a resolved, launched brand. Unknown hosts resolve to DEFAULT with status
    // "unknown" — do NOT brand those as Club Astra. Draft brands are absent from BRAND_DISPLAY.
    const display = status === 'resolved' ? BRAND_DISPLAY[eventId] : null;
    if (!display) return; // neutral static head stands

    document.title = display.name + DOC_TITLE_SUFFIX;
    setMeta('theme-color', display.themeColor);
    setMeta('apple-mobile-web-app-title', display.shortName || display.name);
    setLink('apple-touch-icon', display.icon);
    setLink('icon', display.icon);
    addPreconnect(display.icon);
    preloadImage(display.icon);
    if (display.logo) {
      addPreconnect(display.logo);
      preloadImage(display.logo);
    }
    if (display.logoSymbol) {
      addPreconnect(display.logoSymbol);
      preloadImage(display.logoSymbol);
    }
    setLink('manifest', `/manifest.webmanifest?event=${encodeURIComponent(eventId)}`);
  } catch {
    /* head branding is best-effort — never block boot */
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  applyBrandHead(window.location.hostname, window.location.search);
}
